import { Link } from "@tanstack/react-router";
import { memo, useMemo } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import ProfileCard from "@/components/profile-card";
import {
  HeartIconInactive,
  ThumbsDownInactiveIcon,
  ThumbsUpInactiveIcon,
} from "@/components/rate-options/icons";
import { Separator } from "@/components/ui/separator";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import { OverallRatingEnum } from "@/pages/search/types";

import type { GameFriendType } from "../types";
import { SummaryIcon } from "./icons";

interface UserProps {
  user: GameFriendType;
}

function User({ user }: UserProps) {
  const isDesktop = useIsBreakPoint("md");

  const badge = useMemo(() => {
    if (!user.escapeReview) {
      return null;
    }

    const icons: React.ReactNode[] = [];

    icons.push();

    if (user.escapeReview.overallRating === OverallRatingEnum.DISLIKE) {
      icons.push(
        <ThumbsDownInactiveIcon className="h-[9px] w-2.5" init key="rating" />
      );
    }
    if (user.escapeReview.overallRating === OverallRatingEnum.LIKE) {
      icons.push(
        <ThumbsUpInactiveIcon className="h-[9px] w-2.5" init key="rating" />
      );
    }
    if (user.escapeReview.overallRating === OverallRatingEnum.LOVE) {
      icons.push(
        <HeartIconInactive className="h-[9px] w-2.5" init key="rating" />
      );
    }
    if (user.escapeReview.hasSummary) {
      icons.push(<SummaryIcon key="summary" />);
    }

    if (!icons.length) {
      return null;
    }

    return (
      <div className="absolute -top-[3px] left-[33px] flex h-[17px] items-center gap-[3px] rounded-[9px] bg-white px-1 shadow-0-0-4 shadow-black">
        {icons}
      </div>
    );
  }, [user.escapeReview]);

  return (
    <ProfileCard userId={user.id}>
      <div className="flex w-12.5 flex-col items-stretch gap-[5px]">
        <div className="relative h-12.5">
          <Link
            className="link-overlay"
            target={isDesktop ? "_blank" : undefined}
            to={`/@${user.username}`}
          >
            <CloudinaryImage
              className="size-12.5 rounded-full"
              rW={100}
              size={50}
              user={user}
            />
          </Link>
          {badge}
        </div>
        <p className="line-clamp-1 text-center text-11/[1.2em] text-white md:hidden">
          {user.username}
        </p>
      </div>
    </ProfileCard>
  );
}

interface PlayersProps {
  title: string;
  users: GameFriendType[];
}

const USER_LIMIT = 10;

function Players({ title, users }: PlayersProps) {
  let renderUsers = users;
  let renderMore = 0;

  if (users.length > USER_LIMIT) {
    renderUsers = users.slice(0, USER_LIMIT);
    renderMore = users.length - USER_LIMIT;
  }

  return (
    <div className="mb-10 md:mb-7.5">
      <h3 className="text-20 text-white leading-[1.2em] md:text-24">{title}</h3>
      <div className="flex flex-wrap gap-2.5 pt-5">
        {renderUsers.map((user) => (
          <User key={user.id} user={user} />
        ))}
        {renderMore > 0 && (
          <div className="flex size-12.5 items-center justify-center rounded-full border border-white bg-deep-cove-dark font-medium text-16 text-white">
            +{renderMore}
          </div>
        )}
      </div>
      <Separator className="mt-7.5 hidden bg-white/20 md:block" />
    </div>
  );
}

export default memo(Players);
